import { useQuery } from "react-query"
import { useTranslation } from "react-i18next"
import { fetchOrderMissingStockDiagnosis } from "../../services"
import { formatCalendarDate } from "../../utils/warehouseDates"

// Statuses that mean "nothing to show this user" rather than a failure: 401/403 a reader who is not
// staff, 404 a platform without the route yet.
const SILENT_STATUSES = [401, 403, 404]

// The copy ships in es and pt only; a browser in any other language reads es, so the dates must too.
const bundleLanguage = (language) => (["es", "pt"].includes((language || "").slice(0, 2)) ? language.slice(0, 2) : "es")

// A lot date is a calendar date stored as midnight UTC: take its UTC day and format that, so no
// browser zone moves it to the day before.
const formatDate = (value, language) => {
    if (!value) return "—"
    const date = new Date(value)
    if (isNaN(date.getTime())) return "—"
    return formatCalendarDate(date.toISOString().slice(0, 10), bundleLanguage(language), { year: "numeric", month: "2-digit", day: "2-digit" }) || "—"
}

// Why a backordered order is not getting stock, for warehouse staff: per short product, the rule
// that keeps each unit out and the order's-warehouse locations holding the blocked units, so ops can
// pull expired stock or move what sits in a non-sellable bin. Backed by
// GET /orders/wms/:id/missing-stock-diagnosis, which is staff-only.
// orderUpdatedAt is in the key so the diagnosis is read again whenever the order itself changes.
export default function MissingStockDiagnosisSection({ orderId, orderStatus, orderUpdatedAt }) {
    const isBackorder = orderStatus === "backorder"
    const { data, error } = useQuery(["orderMissingStockDiagnosis", orderId, orderUpdatedAt], () => fetchOrderMissingStockDiagnosis(orderId), {
        enabled: isBackorder && !!orderId,
        // A refused or missing route is final; retrying would only repeat the request.
        retry: false,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    })

    if (!isBackorder) return null
    // Anything but a silent status is a failure the operator should know about rather than a
    // section that never appears.
    const loadFailed = !!error && !SILENT_STATUSES.includes(error.response?.status)
    return <MissingStockDiagnosisView diagnosis={data} loadFailed={loadFailed} />
}

export function MissingStockDiagnosisView({ diagnosis, loadFailed = false }) {
    const { t, i18n } = useTranslation()
    if (loadFailed) {
        return (
            <div className="even:bg-gray-50 bg-white px-4 py-5 sm:px-6" data-testid="missing-stock-diagnosis-error">
                <p className="text-base font-medium text-gray-500 m-0">{t("missing_stock.section_title")}</p>
                <p className="text-sm text-red-600 mt-1 mb-0">{t("missing_stock.load_error")}</p>
            </div>
        )
    }

    const lines = diagnosis?.diagnosable ? diagnosis.lines || [] : []
    if (lines.length === 0) return null

    return (
        <div className="even:bg-gray-50 bg-white px-4 py-5 sm:px-6" data-testid="missing-stock-diagnosis">
            <p className="text-base font-medium text-gray-500 m-0">{t("missing_stock.section_title")}</p>
            <ul className="mt-3 space-y-4 list-none p-0">
                {lines.map((line) => (
                    <li key={`${line.product_id || "unknown"}-${(line.order_line_ids || []).join("-")}`} className="border border-gray-200 rounded p-3 bg-white" data-testid="missing-stock-line">
                        <LineHeader line={line} t={t} />
                        <p className="text-sm text-gray-800 mt-2 mb-0">{t(`missing_stock.reasons.${line.reason}`, { defaultValue: t("missing_stock.unknown_reason") })}</p>
                        {line.assignable_now && <p className="text-sm text-blue-700 mt-1 mb-0" data-testid="missing-stock-assignable">{t("missing_stock.assignable_hint")}</p>}
                        <Buckets buckets={line.buckets} t={t} />
                        <LotsDetail details={line.lots_detail} t={t} language={i18n.language} />
                    </li>
                ))}
            </ul>
        </div>
    )
}

function LineHeader({ line, t }) {
    return (
        <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
                <p className="m-0 text-sm font-medium text-gray-900">{line.name || line.sku}</p>
                {line.name && line.sku && <p className="m-0 text-xs text-gray-500">{line.sku}</p>}
            </div>
            <div className="text-xs text-gray-700 text-right">
                {line.missing_quantity > 0 && <p className="m-0 font-semibold">{t("missing_stock.missing", { count: line.missing_quantity })}</p>}
                {line.blocked_quantity > 0 && <p className="m-0">{t("missing_stock.blocked", { count: line.blocked_quantity })}</p>}
                {line.in_transit_quantity > 0 && <p className="m-0">{t("missing_stock.in_transit", { count: line.in_transit_quantity })}</p>}
            </div>
        </div>
    )
}

// A rule code the platform adds later reads as "another engine rule", never as a raw key.
const bucketLabel = (bucket, t) => t(`missing_stock.buckets.${bucket}`, { defaultValue: t("missing_stock.buckets.other_engine_rule") })

function Buckets({ buckets, t }) {
    const entries = Object.entries(buckets || {})
    if (entries.length === 0) return null

    return (
        <div className="flex flex-wrap gap-2 mt-2">
            {entries.map(([bucket, quantity]) => (
                <span key={bucket} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {bucketLabel(bucket, t)}: {quantity}
                </span>
            ))}
        </div>
    )
}

function LotsDetail({ details, t, language }) {
    if (!details || details.length === 0) return null

    return (
        <table className="w-full text-xs text-gray-700 mt-3">
            <thead>
                <tr className="text-left text-gray-500">
                    <th className="font-medium pr-2">{t("missing_stock.lot")}</th>
                    <th className="font-medium pr-2">{t("missing_stock.rule")}</th>
                    <th className="font-medium pr-2">{t("missing_stock.expiration_date")}</th>
                    <th className="font-medium pr-2">{t("missing_stock.sellable_until")}</th>
                    <th className="font-medium pr-2">{t("missing_stock.units")}</th>
                    <th className="font-medium">{t("missing_stock.locations")}</th>
                </tr>
            </thead>
            <tbody>
                {details.map((detail, index) => (
                    // Two lots can share a code, and lotless rows have none: the index keeps keys unique.
                    <tr key={`${index}-${detail.unique_code || "no-lot"}-${detail.warehouse_id}-${detail.bucket}`} className="align-top">
                        <td className="pr-2">{detail.unique_code || t("missing_stock.no_lot")}</td>
                        <td className="pr-2">{bucketLabel(detail.bucket, t)}</td>
                        <td className="pr-2">{formatDate(detail.expiration_date, language)}</td>
                        <td className="pr-2">{formatDate(detail.sellable_until, language)}</td>
                        <td className="pr-2">{detail.quantity}</td>
                        <td><Locations locations={detail.locations} warehouseId={detail.warehouse_id} t={t} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

// The backend only sends locations for the order's own warehouse. A row without them is in another
// warehouse only when it HAS a warehouse: a unit with no location (in reception, presale not yet
// received) has none, and reads "—" next to its own rule.
function Locations({ locations, warehouseId, t }) {
    if (!locations && (warehouseId === null || warehouseId === undefined)) return <span className="text-gray-400">—</span>
    if (!locations) return <span className="text-gray-400">{t("missing_stock.other_warehouse_locations")}</span>

    return (
        <ul className="list-none p-0 m-0">
            {locations.map((location) => (
                <li key={location.id ?? "no-location"}>
                    {location.unique_code || t("missing_stock.no_location")} · {location.quantity}
                </li>
            ))}
        </ul>
    )
}
